"use client";

import * as React from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { useRightTabs } from "@/contexts/right-tabs-context";
import { useLeftSidebar } from "@/contexts/left-sidebar-context";

interface MainContentWrapperProps {
  children: React.ReactNode;
}

/**
 * MainContentWrapper - Handles dynamic spacing for left and right sidebars
 *
 * Mobile-first approach:
 * - Mobile (< lg): Full width, no sidebar offsets (sidebars are overlays)
 * - Desktop (>= lg): Fixed margins for always-visible sidebars
 *
 * @author Ellis Reed
 */
export function MainContentWrapper({ children }: MainContentWrapperProps) {
  const { isExpanded: rightExpanded } = useRightTabs();
  const { isExpanded: leftExpanded } = useLeftSidebar();
  const pathname = usePathname();

  // Hide main site navigation on admin pages
  const isAdminPage = pathname?.startsWith("/admin");

  // If on admin page, don't add sidebar padding (admin has its own layout)
  if (isAdminPage) {
    return <div className="w-full">{children}</div>;
  }

  // Mobile-first responsive padding
  // Mobile: No left padding (sidebar overlays on mobile)
  // Desktop (md+): Left padding shifts when sidebar expands (same as right sidebar)
  // Right sidebar: Always shifts content on desktop
  const leftPadding = leftExpanded ? "md:pl-64" : "md:pl-16"; // 256px expanded or 64px collapsed on desktop
  const rightPadding = rightExpanded ? "md:pr-80" : "md:pr-16"; // 320px expanded or 64px collapsed on desktop

  return (
    <div className={cn(
      "flex flex-col w-full pt-16",
      leftPadding,
      rightPadding,
      "transition-all duration-300 ease-in-out", // Smooth transitions when sidebars toggle
      "relative z-10", // Below sidebar toggle buttons (z-45) so they remain clickable
      "bg-transparent" // Transparent to let cityscapes show through
    )}>
      {children}
    </div>
  );
}